/**
 * Centrální design tokeny — jediný zdroj pravdy pro vizuální vrstvu.
 * Zrcadleno v tokens.css jako CSS custom properties.
 * Nepřidávat barvy mimo tento soubor; komponenty čerpají odsud
 * nebo přes newTheme.ts / muiTheme.ts.
 *
 * Aplikace má čtyři vzhledy (viz contexts/ThemeModeContext.tsx): výchozí,
 * "luxusní" (světlý, zlato-krémový, editorial), "dolarový" (tmavě
 * zelený, inspirovaný barvou dolarové bankovky) a "půlnoční" (sytá tmavá
 * navy přímo z barvy Apex loga #0A1E3C). Kromě škály
 * `primary` a `neutral` se luxusní vzhled navíc liší i `radius` (hranatější,
 * méně zaoblené rohy), `shadow` (jemnější, teple tónované stíny místo
 * výrazných navy stínů) a `font.display` (serifové písmo pro nadpisy
 * a hlavičky karet, viz muiTheme.ts a styles/cards.ts) — dolarový a půlnoční
 * vzhled mění pouze barevnou škálu. Sémantické barvy (success/warning/error)
 * i `white`/`sellRow` zůstávají shodné napříč vzhledy, aby si stavy
 * pokynů apod. držely význam.
 */

const defaultColorTokens = {
  // Primární škála odvozená z brand #11457F (kotva na stupni 700).
  primary: {
    50: '#F2F6FB',
    100: '#E1EBF6',
    200: '#C3D6EC',
    300: '#94B5DB',
    400: '#5F8FC4',
    500: '#33699F',
    600: '#1D5590',
    700: '#11457F',
    800: '#0C3563',
    900: '#082645',
  },
  // Neutrální škála s lehkým studeným nádechem, aby ladila s navy.
  neutral: {
    50: '#F7F8FA',
    100: '#EFF1F4',
    200: '#E2E5EA',
    300: '#CBD0D8',
    400: '#9AA1AC',
    500: '#6A7280',
    600: '#4D545F',
    700: '#3A4048',
    800: '#272C33',
    900: '#171A1E',
  },
  success: { 700: '#1E6614', 500: '#2A7E19', 100: '#E4F3DF' },
  warning: { 700: '#925708', 500: '#D97706', 100: '#FCF0DC' },
  error: { 700: '#A82509', 500: '#D02F0B', 100: '#FBE9E4' },
  white: '#FFFFFF',
  // Teplé podbarvení SELL řádků (nahrazuje #f9f3e5, drží význam).
  sellRow: '#FAF4E7',
}

// Luxusní světlý vzhled: krémovo-zlatá paleta místo studené navy.
const luxuryColorTokens = {
  primary: {
    50: '#FAF5EA',
    100: '#F2E8CF',
    200: '#E4D1A0',
    300: '#D0B36D',
    400: '#BC9A4E',
    500: '#A67F3C',
    600: '#8F6830',
    700: '#795627',
    800: '#5E4420',
    900: '#443119',
  },
  neutral: {
    50: '#FBF8F3',
    100: '#F5EFE4',
    200: '#E9E0D0',
    300: '#D6C9B0',
    400: '#B3A489',
    500: '#8C7C63',
    600: '#6B5D48',
    700: '#4C4234',
    800: '#332C22',
    900: '#1D1913',
  },
  success: defaultColorTokens.success,
  warning: defaultColorTokens.warning,
  error: defaultColorTokens.error,
  white: defaultColorTokens.white,
  sellRow: defaultColorTokens.sellRow,
}

// Dolarový vzhled: tmavě zelená paleta inspirovaná barvou dolarové bankovky.
const dollarColorTokens = {
  primary: {
    50: '#EBF2ED',
    100: '#CFE0D5',
    200: '#A3C7AE',
    300: '#71A67F',
    400: '#4A8258',
    500: '#2F6142',
    600: '#1F4A34',
    700: '#153A29',
    800: '#0F2C1F',
    900: '#0A1F16',
  },
  neutral: {
    50: '#F6F7F5',
    100: '#EDEFEA',
    200: '#DFE3DB',
    300: '#C7CDC0',
    400: '#9CA697',
    500: '#707B6C',
    600: '#535D50',
    700: '#3E453B',
    800: '#2A2F27',
    900: '#181B16',
  },
  success: defaultColorTokens.success,
  warning: defaultColorTokens.warning,
  error: defaultColorTokens.error,
  white: defaultColorTokens.white,
  sellRow: defaultColorTokens.sellRow,
}

// Půlnoční vzhled: sytá tmavá navy přímo z barvy Apex loga (#0A1E3C, viz
// assets/APEX-CAPITAL.svg). Accent (tlačítka = primary[700]) leží přesně na
// logo barvě; ostatní stupně jsou dopočtené ztmavení/zesvětlení kolem ní.
const midnightColorTokens = {
  primary: {
    50: '#EEF2F8',
    100: '#CBD9EA',
    200: '#93AFD0',
    300: '#567FB0',
    400: '#2A588C',
    500: '#123F6E',
    600: '#0C2C50',
    700: '#0A1E3C',
    800: '#07152B',
    900: '#040D1C',
  },
  neutral: {
    50: '#F6F7FA',
    100: '#EDEFF4',
    200: '#DFE3EC',
    300: '#C7CDDA',
    400: '#99A1B3',
    500: '#6A7286',
    600: '#4C5464',
    700: '#383F4C',
    800: '#262B34',
    900: '#161A20',
  },
  success: defaultColorTokens.success,
  warning: defaultColorTokens.warning,
  error: defaultColorTokens.error,
  white: defaultColorTokens.white,
  sellRow: defaultColorTokens.sellRow,
}

const bodyFontFamily =
  'Poppins, "Segoe UI", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'

const defaultShapeTokens = {
  radius: {
    sm: '6px',
    md: '10px',
    lg: '16px',
    full: '9999px',
  },
  // Stíny tónované brand navy (#082645), ne generickou černou.
  shadow: {
    sm: '0 1px 2px rgba(8, 38, 69, 0.06)',
    md: '0 4px 12px rgba(8, 38, 69, 0.10)',
    lg: '0 12px 32px rgba(8, 38, 69, 0.18)',
  },
  fontDisplay: bodyFontFamily,
}

// Luxusní vzhled: hranatější rohy a plošší, tepleji tónované stíny
// (editorial styl místo výrazných navy stínů) + serifová hlavičková
// typografie (nadpisy, hlavičky karet) — viz fonts/fonts.css.
const luxuryShapeTokens = {
  radius: {
    sm: '2px',
    md: '4px',
    lg: '8px',
    full: '9999px',
  },
  shadow: {
    sm: '0 1px 2px rgba(94, 68, 32, 0.05)',
    md: '0 2px 8px rgba(94, 68, 32, 0.08)',
    lg: '0 8px 24px rgba(94, 68, 32, 0.14)',
  },
  fontDisplay: '"Playfair Display", Georgia, "Times New Roman", serif',
}

export const tokens = {
  color: defaultColorTokens,
  radius: defaultShapeTokens.radius,
  shadow: defaultShapeTokens.shadow,
  transition: {
    fast: '120ms ease-out',
    base: '200ms ease-out',
  },
  focus: {
    // Viditelný focus ring pro klávesnicovou navigaci (WCAG 2.4.7).
    ring: '2px solid #33699F',
    ringOffset: '2px',
    shadow: '0 0 0 3px rgba(51, 105, 159, 0.35)',
  },
  font: {
    family: bodyFontFamily,
    display: defaultShapeTokens.fontDisplay,
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
}

export type Tokens = typeof tokens
export type ThemeMode = 'default' | 'luxury' | 'dollar' | 'midnight'

const colorTokensByMode: Record<ThemeMode, typeof defaultColorTokens> = {
  default: defaultColorTokens,
  luxury: luxuryColorTokens,
  dollar: dollarColorTokens,
  midnight: midnightColorTokens,
}

// Mění barevnou paletu i tvarosloví (rohy/stíny/nadpisové písmo) tokenů
// "na místě" (stejná reference `tokens`), aby je zachytily i moduly, které
// si `tokens`/odvozené objekty naimportovaly staticky.
export function applyThemeTokens(mode: ThemeMode) {
  const shape = mode === 'luxury' ? luxuryShapeTokens : defaultShapeTokens
  tokens.color = colorTokensByMode[mode]
  tokens.radius = shape.radius
  tokens.shadow = shape.shadow
  tokens.font = { ...tokens.font, display: shape.fontDisplay }
}
